import { API } from 'Amplify';

export enum ApiClientStatusEnum {
  Idle,
  Busy,
  Done,
  Error,
}

export enum ApiClientRequestTypeEnum {
  GET,
  POST,
}

export interface ApiClientStatus<T> {
  clientStatus: ApiClientStatusEnum;
  data: T | null;
  error: any | null;
}

export type ApiStatusListener<T> = (status: ApiClientStatus<T>) => void;
export type ApiClientStatusUnsubscribeListener = () => void;

/**
 * ApiClient is responsible for performing an API call and ensuring that only one API call is made
 * despite re-rendering of React components, essentially debouncing multiple calls to it.
 *
 * It uses an observer pattern to notify registered listeners about the status of the API call.
 */
export abstract class ApiClient<T> {
  protected status: ApiClientStatus<T> = {
    clientStatus: ApiClientStatusEnum.Idle,
    data: null,
    error: null,
  };

  protected listeners = new Set<ApiStatusListener<T>>();
  protected readonly apiClientRequestType: ApiClientRequestTypeEnum;

  protected constructor(apiClientRequestType = ApiClientRequestTypeEnum.GET) {
    this.apiClientRequestType = apiClientRequestType;
  }

  /**
   * Installs a new device configuration listener and returns the unsubscribe function.
   */
  public subscribe(listener: ApiStatusListener<T>): ApiClientStatusUnsubscribeListener {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  protected notifyListeners() {
    this.listeners.forEach((listener) => listener(this.status));
  }

  public getStatus() {
    return this.status;
  }

  /**
   * This method is called before the API call is made, it performs any necessary actions
   * and returns the body of the data to be used in the API request.
   */
  protected abstract preApiInvocationAction(): Promise<any>;

  protected abstract postApiInvocationAction(data: T | null): Promise<void>;

  protected abstract apiInvocationErrorAction(error: any): Promise<void>;

  protected abstract getApiUri(): string;

  public resetState() {
    this.status.clientStatus = ApiClientStatusEnum.Idle;
  }

  public async fetchData(_thingName: string) {
    switch (this.status.clientStatus) {
      case ApiClientStatusEnum.Busy:
      case ApiClientStatusEnum.Error:
      case ApiClientStatusEnum.Done:
        return;

      case ApiClientStatusEnum.Idle:
        break;

      default:
        break;
    }

    this.status.error = null;
    this.status.clientStatus = ApiClientStatusEnum.Busy;

    const apiRequestData = await this.preApiInvocationAction();

    try {
      this.notifyListeners();

      switch (this.apiClientRequestType) {
        case ApiClientRequestTypeEnum.GET:
          this.status.data = await API.get('IO_API', this.getApiUri(), apiRequestData);
          break;

        case ApiClientRequestTypeEnum.POST:
          this.status.data = await API.post('IO_API', this.getApiUri(), apiRequestData);
          break;

        default:
          break;
      }

      this.status.clientStatus = ApiClientStatusEnum.Done;

      await this.postApiInvocationAction(this.status.data);

      this.notifyListeners();
    } catch (e: any) {
      this.status.data = null;
      this.status.clientStatus = ApiClientStatusEnum.Error;
      this.status.error = e?.response?.data?.body || e;

      await this.apiInvocationErrorAction(e);

      this.notifyListeners();
    }
  }
}

/**
 * ThingsApiClient is responsible for performing an API call and ensuring that only one API call is made
 * despite re-rendering of React components, essentially debouncing multiple calls to it.
 *
 * It uses an observer pattern to notify registered listeners about the status of the API call.
 *
 * @note It supports only ONE thingName at a time.
 */
export abstract class ThingsApiClient<T> extends ApiClient<T> {
  protected thingName: string | null = null;
  protected jobQueue: Array<string> = [];
  protected readonly isJobEnqueueingSupported: boolean = false;

  protected constructor(isJobEnqueueingSupported: boolean = false, apiClientRequestType = ApiClientRequestTypeEnum.GET) {
    super(apiClientRequestType);
    this.isJobEnqueueingSupported = isJobEnqueueingSupported;
  }

  protected enqueueJob(thingName: string) {
    this.jobQueue.push(thingName);
  }

  popOldestEnqueuedJobIfExistsAndTriggerAsyncFetch() {
    if (this.jobQueue.length >= 1) {
      const nextThingName = this.jobQueue.shift();
      if (nextThingName) {
        this.fetchData(nextThingName);
      }

      return true;
    }

    return false;
  }

  /**
   * This method is called before the API call is made, it performs any necessary actions
   * and returns the body of the data to be used in the API request.
   */
  protected abstract preApiInvocationAction(): Promise<any>;

  protected abstract postApiInvocationAction(data: T | null): Promise<void>;

  public async forceFetchData() {
    if (!this.thingName) {
      return;
    }

    this.resetState();
    await this.fetchData(this.thingName);
  }

  public async fetchData(thingName: string) {
    switch (this.status.clientStatus) {
      case ApiClientStatusEnum.Busy:
        if (this.isJobEnqueueingSupported && this.thingName !== thingName) {
          this.enqueueJob(thingName);
        }
        return;

      case ApiClientStatusEnum.Error:
      case ApiClientStatusEnum.Done:
        if (this.thingName !== thingName) {
          this.thingName = thingName;
        } else {
          return;
        }
        break;

      case ApiClientStatusEnum.Idle:
        this.thingName = thingName;
        break;

      default:
        break;
    }

    this.status.error = null;
    this.status.clientStatus = ApiClientStatusEnum.Busy;

    const apiRequestData = await this.preApiInvocationAction();

    try {
      this.notifyListeners();

      switch (this.apiClientRequestType) {
        case ApiClientRequestTypeEnum.GET:
          this.status.data = await API.get('IO_API', this.getApiUri(), apiRequestData);
          break;

        case ApiClientRequestTypeEnum.POST:
          this.status.data = await API.post('IO_API', this.getApiUri(), apiRequestData);
          break;

        default:
          break;
      }

      this.status.clientStatus = ApiClientStatusEnum.Done;

      await this.postApiInvocationAction(this.status.data);

      this.popOldestEnqueuedJobIfExistsAndTriggerAsyncFetch() || this.notifyListeners();
    } catch (e: any) {
      this.status.data = null;
      this.status.clientStatus = ApiClientStatusEnum.Error;
      this.status.error = e?.response?.data?.body || e;

      this.popOldestEnqueuedJobIfExistsAndTriggerAsyncFetch() || this.notifyListeners();
    }
  }
}
